import { useNavigate } from "react-router-dom";
import { driverService } from "@/services/api";
import { relativeTime, fmtKm } from "@/lib/format";
import { StatusBadge } from "./StatusBadge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { X, MapPin, Gauge, User, Navigation, AlertTriangle } from "lucide-react";
import { BatteryStatus } from "./BatteryStatus";
import { useVehicleAddress } from "@/hooks/useVehicleAddress";

// Contextual side panel that slides in when a vehicle marker is selected.
export function VehiclePanel({ vehicle, onClose, className }) {
  const navigate = useNavigate();
  // Before the `!vehicle` bail-out — hooks cannot sit after a conditional
  // return. Falls back to the coordinate label the vehicle payload carries.
  const { address } = useVehicleAddress(
    vehicle?.id,
    vehicle?.location?.lat,
    vehicle?.location?.lng,
    { moving: vehicle?.status === "moving" },
  );
  if (!vehicle) return null;
  const driver = vehicle.driverId ? driverService.byId(vehicle.driverId) : null;

  return (
    <div
      data-testid="vehicle-panel"
      className={cn(
        "flex h-full w-full flex-col overflow-hidden rounded-2xl border border-border/70 bg-card/80 backdrop-blur-xl animate-slide-in-right",
        className
      )}
    >
      <div className="flex items-start justify-between border-b border-border/60 p-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="font-display text-lg font-semibold">{vehicle.name}</h3>
          </div>
          <div className="mt-0.5 flex items-center gap-2 text-xs text-muted-foreground">
            <span>{vehicle.fleetTag}</span>
            <span>·</span>
            <StatusBadge status={vehicle.status} size="sm" />
          </div>
        </div>
        <button onClick={onClose} className="rounded-lg p-1 text-muted-foreground transition hover:bg-secondary hover:text-foreground" data-testid="vehicle-panel-close">
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="flex-1 space-y-4 overflow-y-auto p-4 no-scrollbar">
        <div className="grid grid-cols-2 gap-3">
          <div className="rounded-xl border border-border/60 bg-secondary/30 p-3">
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground"><Gauge className="h-3.5 w-3.5" /> Speed</div>
            <div className="mt-1 font-display text-2xl font-semibold tabular-nums">{vehicle.speed}<span className="ml-1 text-sm text-muted-foreground">mph</span></div>
          </div>
          <div className="rounded-xl border border-border/60 bg-secondary/30 p-3">
            <div className="text-xs text-muted-foreground">Battery</div>
            <div className="mt-1.5"><BatteryStatus status={vehicle.batteryStatus} /></div>
          </div>
        </div>

        <Row icon={MapPin} label="Location" value={address || vehicle.location.address} />
        <Row icon={User} label="Driver" value={driver ? driver.name : "Unassigned"} />
        <Row icon={Navigation} label="Today's trip" value={fmtKm(vehicle.todayDistance)} />

        <div className="rounded-xl border border-border/60 bg-secondary/20 p-3 text-xs text-muted-foreground">
          <div className="flex justify-between"><span>VIN</span><span className="font-telemetry text-foreground/80">{vehicle.vin}</span></div>
          <div className="mt-1.5 flex justify-between"><span>Device</span><span className="text-foreground/80">{vehicle.device.id}</span></div>
          <div className="mt-1.5 flex justify-between"><span>Last updated</span><span className="text-foreground/80">{relativeTime(vehicle.lastSeen)}</span></div>
        </div>

        {vehicle.alerts > 0 && (
          <div className="flex items-center gap-2 rounded-xl border border-red-500/25 bg-red-500/10 p-3 text-sm text-red-700 dark:text-red-300">
            <AlertTriangle className="h-4 w-4" />
            {vehicle.alerts} active alert{vehicle.alerts > 1 ? "s" : ""}
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 gap-2 border-t border-border/60 p-4">
        <Button onClick={() => navigate(`/vehicles/${vehicle.id}`)} data-testid="panel-view-vehicle">View Vehicle</Button>
        <Button variant="secondary" onClick={() => navigate(`/vehicles/${vehicle.id}?tab=live`)}>Show Route</Button>
      </div>
    </div>
  );
}

function Row({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-2.5">
      <Icon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
      <div className="min-w-0">
        <div className="text-xs text-muted-foreground">{label}</div>
        <div className="truncate text-sm font-medium">{value}</div>
      </div>
    </div>
  );
}
